import { type CSSProperties, type ReactNode } from 'react'
import { type PopperProps } from '@mui/material/Popper'
import { type TooltipProps } from '@mui/material/Tooltip'
import { type TransitionProps } from '@mui/material/transitions/transition'
import type {
  IAvatarProps,
  IButtonProps,
  IDropdownListProps,
  IIconButtonProps,
  IIconProps,
  IIconsVector,
  ITooltipProps,
  TextStylesType,
} from 'shared/ui'
import type { ITooltipPlacementProps } from 'shared/ui/Tooltip'
import type { ITextFieldProps } from 'shared/ui/TextField'
import { type ZIndex } from 'shared/constants/zIndex'

export enum EnumDropdownItemVariant {
  Default = 'default',
  Checkbox = 'checkbox',
  CheckedRight = 'checked-right',
  CheckedLeft = 'checked-left',
  Deletable = 'deletable',
  Custom = 'custom',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type IDropdownProps<T = any> = {
  PopperProps?: Partial<PopperProps | null>
  TransitionProps?: Partial<TransitionProps | null>
  icon?: IIconsVector
  withSearch?: boolean
  withTextInput?: boolean
  bottomActions?: (onCloseMenu: () => void, dropdownId: string) => React.ReactNode
  topListComponent?: (onCloseMenu: () => void, dropdownId: string) => React.ReactNode
  underControlComponent?: (onCloseMenu: () => void, dropdownId: string) => React.ReactNode
  width?: CSSProperties['width']
  childContainerWidth?: number
  maxHeight?: number
  margin?: number | string
  marginTop?: number | string
  marginLeft?: number | string
  placement?: TooltipProps['placement']
  typeButton?: 'icon-button' | 'button'
  sizeTriggerButton?: IIconButtonProps['size']
  variantTriggerButton?: IIconButtonProps['variant']
  triggerComponent?: (open: boolean) => ReactNode
  items?: IDropdownItem<T>[]
  parent?: IDropdownItem<T>
  onChange?: (item: IDropdownItem<T>, child?: IDropdownItem<T>) => void
  onPressEnter?: (text: string) => void
  onToggle?: (status: boolean) => void
  onToggleHover?: (status: boolean) => void
  buttonProps?: IButtonProps
  iconButtonProps?: IIconButtonProps
  tooltipProps?: ITooltipProps
  customComponent?: (onCloseMenu: () => void, dropdownId: string) => ReactNode
  disabled?: boolean
  show?: boolean
  onClose?: () => void
  isHover?: boolean
  parentsDropdownId?: string[]
  onChangeOpen?: (status: boolean) => void
  className?: string
  textFieldProps?: ITextFieldProps
  overlayModalsIds?: string[]
  autoOpen?: boolean
  disableStopPropagation?: boolean
  ariaLabel?: string
  zIndex?: ZIndex
  propsDropdownList?: Partial<IDropdownListProps>
  withToggleTriggerOpen?: boolean
  placeholder?: string
  checkIsCanOpen?: () => Promise<boolean>
  tabIndex?: number
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type IDropdownItem<T = any> = {
  id: number | string
  searchPattern?: string
  group?: string
  height?: CSSProperties['height']
  value?: string
  data?: T
  customFields?: Record<string, string | boolean | number>
  avatarProps?: IAvatarProps
  iconR?: IIconsVector
  iconL?: IIconsVector
  customIconL?: ReactNode
  label: string
  description?: string
  labelBrackets?: string
  labelCount?: number
  link?: string
  short_label?: string
  labelContent?: ReactNode
  labelWidth?: string | number
  labelRight?: ReactNode
  labelRightVariant?: TextStylesType
  labelRightClassName?: string
  iconLabel?: IIconsVector
  tooltipLabelRightProps?: ITooltipProps
  hotKey?: string
  selectedLabelRight?: string
  wrapLabel?: string
  activeValue?: string | boolean
  noRenderEmptyActiveValue?: boolean
  selected?: boolean
  onClick?: () => void
  childrenDropdownProps?: IDropdownProps
  children?: IDropdownItem[]
  customChildren?: (onCloseMenu: () => void, dropdownId: string) => ReactNode
  disabled?: boolean
  disabledChildren?: boolean
  disabledClosedTooltip?: boolean
  divider?: boolean
  tooltipQuestionProps?: ITooltipProps
  iconQuestionProps?: IIconProps
  tooltipProps?: ITooltipProps
  tooltipLabelPlacement?: ITooltipPlacementProps['placement']
  variant?: EnumDropdownItemVariant
  parentTooltipProps?: ITooltipProps
  isCardClick?: boolean
  ariaLabel?: string
  onDelete?: () => void
  renderOption?: (item: IDropdownItem) => ReactNode
  className?: string
}

export type IItemKeyboardNav = {
  isHighlighted: boolean
  isExpanded: boolean
  onCollapse: () => void
  parentsDropdownId?: string[]
}

export type IGroupKeyboardNav = {
  highlightedId: IDropdownItem['id'] | null
  expandedId: IDropdownItem['id'] | null
  onCollapse: () => void
  parentsDropdownId?: string[]
}

export type IDropdownItemProps = {
  onChange?: (item: IDropdownItem, parent?: IDropdownItem) => void
  onChangeCheckbox?: (status: boolean, item: IDropdownItem) => void
  item: IDropdownItem
  parent?: IDropdownItem
  isSelect?: boolean
  isHover?: boolean
  keyboardNav?: IItemKeyboardNav
  childContainerWidth?: number
  ariaLabel?: string
}

export type IDropdownGroupProps = {
  title?: string
  onChange?: (item: IDropdownItem, parent?: IDropdownItem) => void
  onChangeCheckbox?: (status: boolean, item: IDropdownItem) => void
  items?: IDropdownItem[]
  parent?: IDropdownItem
  isSelect?: boolean
  isScroll?: boolean
  stylesWrap?: CSSProperties
  isMultiSelect?: boolean
  handleMultiSelect?: (items: IDropdownItem[]) => void
  handleMultiDeselect?: (title: string) => void
  showSelectAllButton?: boolean
  showGroupHeader?: boolean
  dropdownItemsByGroup?: [string, IDropdownItem[]][]
  keyboardNav?: IGroupKeyboardNav
}
